import { ProcedureEntity } from "../../../domain/procedures/procedure-entity";
import {
  DefaultMailCfg,
  MailTemplate,
} from "../../../domain/procedures/default-mail-cfg-entity";
import {
  ProcedureMailCfg,
  ProcedureMailCfgType,
} from "../../../domain/procedures/procedure-mail-cfg-entity";
import apiClient from "../../../apis/api-client";
import { default as tenantManagementClient } from "../../../apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import {
  SmartApplyApiError,
  reportUnexpectedError,
  BaseApplicationService,
} from "@graffer-inc/smart-apply";
import { MailStatusMatcher } from "../../../../components/organisms/smart-apply/procedure-detail/mail-cfg";

export type ProcedureMailCfgContextProps = {
  isSubmitting: boolean;
  procedure?: ProcedureEntity;
  orgName?: string;
  mailCfg?: ProcedureMailCfg;
  isMailCfgValid: boolean;
  defaultTemplates?: MailStatusMatcher<MailTemplate>;
  footer: string;
  onMountSetting: typeof ProcedureMailCfgService.prototype.onMountSetting;
  onTemplateEdit: typeof ProcedureMailCfgService.prototype.onTemplateEdit;
  onTestSend: typeof ProcedureMailCfgService.prototype.onTestSend;
  onSave: typeof ProcedureMailCfgService.prototype.onSave;
};

export class ProcedureMailCfgService extends BaseApplicationService<ProcedureMailCfgContextProps> {
  state: ProcedureMailCfgContextProps = {
    isSubmitting: false,
    procedure: undefined as ProcedureEntity | undefined,
    orgName: undefined,
    mailCfg: undefined,
    isMailCfgValid: true,
    defaultTemplates: undefined,
    footer: "",
    onMountSetting: this.onMountSetting.bind(this),
    onTemplateEdit: this.onTemplateEdit.bind(this),
    onTestSend: this.onTestSend.bind(this),
    onSave: this.onSave.bind(this),
  };

  constructor(arg: { procedure: ProcedureEntity }) {
    super();
    this.state.procedure = arg.procedure;
  }

  async onMountSetting(procedure: ProcedureEntity) {
    try {
      await this.fetchDefaultTemplate();
      await this.fetchConfigTemplate(procedure);
    } catch (e) {
      toast.error("手続きメール設定の取得に失敗しました。");
      reportUnexpectedError(e);
      return;
    }
    try {
      const { data: org } = await tenantManagementClient.fetchOrganization(
        procedure.orgId,
      );
      this.setState({
        orgName: org.name,
      });
    } catch (e) {
      toast.error("組織情報の取得に失敗しました。");
      reportUnexpectedError(e);
      return;
    }
  }

  async onTemplateEdit(update: ProcedureMailCfgType) {
    this.setState({
      mailCfg: ProcedureMailCfg.fromJSON(update),
    });
  }

  async onTestSend(
    email: string,
    template: MailTemplate,
    sampleApplication: object,
  ) {
    const testTemplate: MailTemplate = {
      title: template.title,
      body: `${template.body}\n\n${this.state.footer}`,
    };
    try {
      await apiClient.sendTestNotificationMail(
        this.state.procedure!.id,
        email,
        testTemplate,
        sampleApplication,
      );
      toast.success("テストメールが送信されました。");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("テストメールの送信に失敗しました。");
    }
  }

  async onSave() {
    try {
      const { data: saved } = await apiClient.updateProcedureMailCfg(
        this.state.procedure!.id,
        this.state.mailCfg!,
      );
      this.setState({
        mailCfg: ProcedureMailCfg.fromJSON(saved),
      });
      toast.success("手続きメール設定を更新しました。");
    } catch (e) {
      if (
        SmartApplyApiError.isErrorCode(e, "bad_request.invalid_mail_template")
      ) {
        toast.error("メールテンプレートの設定が不正です。");
        return;
      }
      if (SmartApplyApiError.isRetryable(e)) {
        toast.error(
          "ネットワークエラーなどで更新に失敗しました。再度、保存してください。",
        );
        return;
      }
      reportUnexpectedError(e);
      toast.error(e);
    }
  }
  private async fetchDefaultTemplate() {
    const { data } = await apiClient.fetchDefaultMailCfg();
    const defaultCfg = DefaultMailCfg.fromJSON(data);
    const defaultTemplates: MailStatusMatcher<MailTemplate> = {
      RECIEVED: defaultCfg.recievedTemplate!,
      REJECTED: defaultCfg.rejectedTemplate!,
      WITHDRAWN: defaultCfg.withdrawnTemplate!,
      COMPLETED: defaultCfg.completedTemplate!,
    };
    this.setState({
      defaultTemplates: defaultTemplates,
      footer: defaultCfg.footer,
    });
  }
  private async fetchConfigTemplate(procedure: ProcedureEntity) {
    try {
      const { data } = await apiClient.fetchProcedureMailCfg(procedure.id);
      const mailCfg = ProcedureMailCfg.fromJSON(data);
      this.setState({
        procedure: procedure,
        mailCfg: mailCfg,
      });
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "not_found")) {
        this.setState({
          procedure: procedure,
          mailCfg: ProcedureMailCfg.newFrom(procedure),
        });
      } else {
        throw e;
      }
    }
  }
}
